import React, { useState, useEffect } from 'react';
import { Save, Blocks, AlertCircle, Shield, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Switch } from '@/app/components/ui/switch';
import { Label } from '@/app/components/ui/label';
import { toast } from '@/app/components/ui/simple-toaster';
import { useModules, useUpdateModules } from '@/hooks/use-settings';
import { Skeleton } from '@/app/components/ui/skeleton';
import { usePermissions } from '@/hooks/use-permissions';
import { getPlatformModules, getModuleRecommendationNames } from '@/lib/modules';
import type { ModuleName, ModulesConfig } from '@/lib/types';

const GEAR_MODULE_INFO: Record<string, { label: string; description: string }> = {
  assets: {
    label: 'Assets',
    description: 'Core asset management including inventory tracking, status updates, and asset details.',
  },
  inspections: {
    label: 'Inspections',
    description: 'Schedule and record equipment inspections with findings and results.',
  },
  deployments: {
    label: 'Deployments',
    description: 'Track asset check-outs, check-ins, and transfers between locations.',
  },
  rope_cuts: {
    label: 'Rope Cuts',
    description: 'Record and track rope cutting operations and length changes.',
  },
  locations: {
    label: 'Locations',
    description: 'Manage warehouses, sites, and vehicles where assets are stored.',
  },
  maintenance: {
    label: 'Maintenance',
    description: 'Track maintenance records, repairs, and service history.',
  },
  certifications: {
    label: 'Certifications',
    description: 'Manage equipment certifications and compliance documentation.',
  },
  reservations: {
    label: 'Reservations',
    description: 'Allow users to reserve assets for future use.',
  },
  templates: {
    label: 'Asset Templates',
    description: 'Create reusable templates for quick asset creation.',
  },
  reports: {
    label: 'Reports',
    description: 'Generate utilization, compliance, and trend reports.',
  },
};

const GEAR_MODULE_KEYS = Object.keys(GEAR_MODULE_INFO);

export function ModulesPage() {
  const { canManageOrganization } = usePermissions();
  const { data: modulesData, isLoading, error } = useModules();
  const updateModules = useUpdateModules();

  const [localModules, setLocalModules] = useState<ModulesConfig | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  const platformModules = getPlatformModules();

  useEffect(() => {
    if (modulesData?.modules) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLocalModules(modulesData.modules);
    }
  }, [modulesData]);

  useEffect(() => {
    if (modulesData?.modules && localModules) {
      const changed = Object.keys(localModules).some(
        (key) => localModules[key as ModuleName] !== modulesData.modules[key as ModuleName]
      );
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setHasChanges(changed);
    }
  }, [localModules, modulesData]);

  if (!canManageOrganization) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Access Denied</h3>
                <p className="text-sm text-red-800 mt-1">
                  You do not have permission to manage modules. Only administrators can modify these settings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleToggle = (moduleName: ModuleName) => {
    if (!localModules) return;
    setLocalModules({
      ...localModules,
      [moduleName]: !localModules[moduleName],
    });
  };

  const handlePlatformToggle = (moduleKey: string) => {
    if (!localModules) return;
    const moduleName = moduleKey as ModuleName;
    const newEnabled = !localModules[moduleName];

    setLocalModules({ ...localModules, [moduleName]: newEnabled });
  };

  const handleSave = async () => {
    if (!localModules || !modulesData?.modules) return;

    const changes: Partial<ModulesConfig> = {};
    Object.keys(localModules).forEach((key) => {
      const moduleName = key as ModuleName;
      if (localModules[moduleName] !== modulesData.modules[moduleName]) {
        changes[moduleName] = localModules[moduleName];
      }
    });

    try {
      await updateModules.mutateAsync(changes);
      toast.success('Modules Updated', {
        description: 'Module settings have been saved successfully.',
      });
    } catch (_err) {
      toast.error('Error', {
        description: 'Failed to update module settings. Please try again.',
      });
    }
  };

  const handleReset = () => {
    if (modulesData?.modules) {
      setLocalModules(modulesData.modules);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-8 w-1/4" />
        <Skeleton className="h-24" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Error Loading Modules</h3>
                <p className="text-sm text-red-800 mt-1">
                  Failed to load module settings. Please try refreshing the page.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const enabledCount = localModules
    ? Object.values(localModules).filter(Boolean).length
    : 0;
  const totalCount = localModules ? Object.keys(localModules).length : 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Module Management</h1>
          <p className="text-gray-600 mt-1">
            Enable or disable features for your organization ({enabledCount}/{totalCount} enabled)
          </p>
        </div>
        <div className="flex gap-2">
          {hasChanges && (
            <Button variant="outline" onClick={handleReset}>
              Reset
            </Button>
          )}
          <Button onClick={handleSave} disabled={!hasChanges || updateModules.isPending}>
            <Save className="h-4 w-4 mr-2" />
            {updateModules.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900">About Modules</h3>
              <p className="text-sm text-blue-800 mt-1">
                Modules control which features are available to users in your organization. 
                Disabling a module hides it from navigation and restricts access.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* H3Gear Feature Modules */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Blocks className="h-5 w-5" />
            H3Gear Features
          </CardTitle>
          <CardDescription>
            Toggle H3Gear inventory features on or off
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {localModules &&
              GEAR_MODULE_KEYS.map((key) => {
                const moduleName = key as ModuleName;
                const info = GEAR_MODULE_INFO[key];
                if (!info) return null;
                const isEnabled = localModules[moduleName];
                return (
                  <div
                    key={moduleName}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      isEnabled ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800' : 'bg-gray-50 dark:bg-secondary border-gray-200 dark:border-border'
                    }`}
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Label
                          htmlFor={`module-${moduleName}`}
                          className="font-medium text-gray-900"
                        >
                          {info.label}
                        </Label>
                      </div>
                      <p className="text-sm text-gray-600">{info.description}</p>
                    </div>
                    <Switch
                      id={`module-${moduleName}`}
                      checked={isEnabled}
                      onCheckedChange={() => handleToggle(moduleName)}
                    />
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>

      {/* Platform Modules */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Blocks className="h-5 w-5" />
            Platform Modules
          </CardTitle>
          <CardDescription>
            Expand H3ight with additional modules
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {localModules &&
              platformModules.map((mod) => {
                const moduleName = mod.key as ModuleName;
                const isEnabled = localModules[moduleName] ?? false;
                const recNames = getModuleRecommendationNames(mod.key);
                const hasMissingRecs = mod.recommends?.some(
                  (rec) => !localModules[rec as ModuleName]
                );

                return (
                  <div
                    key={mod.key}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      isEnabled ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800' : 'bg-gray-50 dark:bg-secondary border-gray-200 dark:border-border'
                    }`}
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Label
                          htmlFor={`module-${mod.key}`}
                          className="font-medium text-gray-900"
                        >
                          {mod.name}
                        </Label>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded capitalize">
                          {mod.category}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{mod.description}</p>
                      {recNames.length > 0 && hasMissingRecs && isEnabled && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <Info className="h-3.5 w-3.5 text-blue-500" />
                          <p className="text-xs text-blue-700">
                            Works better with: {recNames.join(', ')}
                          </p>
                        </div>
                      )}
                    </div>
                    <Switch
                      id={`module-${mod.key}`}
                      checked={isEnabled}
                      onCheckedChange={() => handlePlatformToggle(mod.key)}
                    />
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
